import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BoutiqueStockDTO, StockRowDTO, StockStatusKey, StockValueKpisDTO } from "@easyshop/shared";
import { Empty, ErrorState, Loading, StockBadge, cx } from "@/components/ui";
import { IosChevron, IosChips, IosGroup, IosRow, MobileIcon, MobileStat, MoreButton } from "@/components/mobile/MobileUi";
import { useSession } from "@/hooks/useSession";
import { useSelectedBoutique } from "@/hooks/useSelectedBoutique";
import { useUi } from "@/hooks/useUi";
import { api } from "@/lib/api";
import { useBoutiqueStock } from "@/lib/queries";
import { categoryKey, groupCategories } from "@/lib/categories";
import { dateFR, dayMonth, eur, eur2, margin, num, relative } from "@/lib/format";

const PAGE = 40;

type SortKey = "status" | "stock" | "sold30" | "soldTotal" | "price" | "supplyPrice" | "name" | "lastRestockAt";
type Filter = "tous" | "rupture" | "surveiller" | "promo" | "sans_vente";

// Filtres et tris du stock d'une boutique.
const FILTERS: { key: Filter; label: string }[] = [
  { key: "tous", label: "Tous" },
  { key: "rupture", label: "Rupture" },
  { key: "surveiller", label: "À surveiller" },
  { key: "promo", label: "Promo" },
  { key: "sans_vente", label: "Sans vente 30 j" },
];

const SORTS: { key: SortKey; label: string }[] = [
  { key: "status", label: "Statut" },
  { key: "stock", label: "Stock" },
  { key: "sold30", label: "Vendu 30 j" },
  { key: "soldTotal", label: "Vendu total" },
  { key: "price", label: "Prix de vente" },
  { key: "supplyPrice", label: "Prix d'achat" },
  { key: "name", label: "Nom" },
  { key: "lastRestockAt", label: "Dernier réassort" },
];

const STATUS_RANK: Record<StockStatusKey, number> = { rupture: 0, surveiller: 1, ok: 2 };

function normalize(s: string) {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

function pick(r: StockRowDTO, k: SortKey): number | string | null {
  if (k === "status") return STATUS_RANK[r.status] * 10 - (r.promo ? 1 : 0);
  return r[k];
}

/** Tri avec les valeurs inconnues toujours en fin de liste, quel que soit le sens. */
function compare(a: number | string | null, b: number | string | null, dir: 1 | -1) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const r = typeof a === "string" && typeof b === "string" ? a.localeCompare(b, "fr") : Number(a) - Number(b);
  return r * dir;
}

const selectClass = "h-9 w-full min-w-0 truncate rounded-[10px] bg-ios-fill px-3 text-[15px]";

/** Produits mobile : valeur du stock, recherche, filtres, tri et fiches produits de la boutique choisie. */
export function MobileStock() {
  const { selectedId } = useSelectedBoutique();
  const { can } = useSession();
  const stock = useBoutiqueStock(selectedId ?? undefined);

  return (
    <div className="flex flex-col gap-[14px]">
      {stock.isLoading && <Loading />}
      {stock.error && <ErrorState error={stock.error} />}
      {stock.data && selectedId && <StockView key={selectedId} boutiqueId={selectedId} data={stock.data} />}
      {can("arrivals.read") && (
        <IosGroup>
          <IosRow to="/m/arrivages">
            <span className="text-[16px]">Arrivages de la boutique</span>
          </IosRow>
        </IosGroup>
      )}
    </div>
  );
}

/** KPI de valeur du stock en grille 2 × 2. */
function StockKpisCompact({ kpis }: { kpis: StockValueKpisDTO }) {
  return (
    <div className="grid grid-cols-2 gap-[10px]">
      <MobileStat
        label="Valeur achat"
        value={eur(kpis.costValue)}
        hint={kpis.unitsWithoutCost > 0 ? `${num(kpis.unitsWithoutCost)} u. sans prix d'achat` : "au prix d'achat"}
      />
      <MobileStat label="Valeur vente" value={eur(kpis.retailValue)} hint="au prix de vente" />
      <MobileStat label="Marge du stock" value={eur(kpis.margin)} hint={kpis.marginPct == null ? "prix d'achat inconnus" : `${margin(kpis.marginPct)} du prix de vente`} />
      <MobileStat label="Articles" value={num(kpis.units)} hint={`${num(kpis.refs)} référence${kpis.refs > 1 ? "s" : ""}`} />
    </div>
  );
}

function StockView({ boutiqueId, data }: { boutiqueId: string; data: BoutiqueStockDTO }) {
  const { can } = useSession();
  const { openProduct } = useUi();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("tous");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "status", dir: 1 });
  const [shown, setShown] = useState(PAGE);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rows = data.products;

  // Catégories regroupées (graphies d'une même catégorie confondues), par ordre alphabétique.
  const categories = useMemo(() => groupCategories(rows.map((r) => r.category)), [rows]);

  const list = useMemo(() => {
    const needle = normalize(search.trim());
    const out = rows.filter((r) => {
      if (needle && !normalize(`${r.name} ${r.category ?? ""}`).includes(needle)) return false;
      if (category && (r.category == null || categoryKey(r.category) !== category)) return false;
      if (filter === "promo") return r.promo != null;
      if (filter === "sans_vente") return r.sold30 <= 0;
      if (filter !== "tous") return r.status === filter;
      return true;
    });
    return out.sort((a, b) => compare(pick(a, sort.key), pick(b, sort.key), sort.dir) || a.name.localeCompare(b.name, "fr"));
  }, [rows, search, filter, category, sort]);

  // Un nouveau filtre repart de la première page.
  const reset = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setShown(PAGE);
  };

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await api.post<BoutiqueStockDTO>(`/boutiques/${boutiqueId}/stock/refresh`);
      qc.setQueryData(["stock", boutiqueId], res);
      await qc.invalidateQueries({ queryKey: ["stock", boutiqueId] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Actualisation impossible");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <label className="flex h-9 items-center gap-[6px] rounded-[10px] bg-ios-fill px-2">
        <MobileIcon name="search" size={16} color="oklch(0.55 0.01 150)" strokeWidth={2.2} />
        <input
          type="search"
          value={search}
          onChange={(e) => reset(setSearch)(e.target.value)}
          placeholder="Rechercher"
          aria-label="Rechercher un produit, une catégorie"
          className="min-w-0 flex-1 bg-transparent text-[17px]"
        />
      </label>
      <IosChips options={FILTERS} value={filter} onChange={reset(setFilter)} />
      <div className="flex items-center gap-2">
        {categories.length > 1 && (
          <select
            aria-label="Catégorie"
            value={category}
            onChange={(e) => reset(setCategory)(e.target.value)}
            className={cx(selectClass, "flex-1", category ? "text-accent" : "text-ink")}
          >
            <option value="">Toutes catégories</option>
            {categories.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label} ({c.count})
              </option>
            ))}
          </select>
        )}
        <select
          aria-label="Trier par"
          value={sort.key}
          onChange={(e) => {
            const key = e.target.value as SortKey;
            reset(setSort)({ key, dir: key === "name" || key === "status" ? 1 : -1 });
          }}
          className={cx(selectClass, "flex-1 text-ink")}
        >
          {SORTS.map((s) => (
            <option key={s.key} value={s.key}>
              Tri : {s.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          aria-label={sort.dir === 1 ? "Ordre croissant" : "Ordre décroissant"}
          onClick={() => reset(setSort)({ ...sort, dir: sort.dir === 1 ? -1 : 1 })}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-ios-fill text-[13px] text-accent"
        >
          {sort.dir === 1 ? "▲" : "▼"}
        </button>
      </div>

      <StockKpisCompact kpis={data.kpis} />

      <div>
        <div className="flex items-baseline gap-2 px-4 pb-[7px] text-[13px] text-muted">
          <span className="uppercase">
            {num(list.length)} produit{list.length > 1 ? "s" : ""}
            {list.length !== rows.length ? ` sur ${num(rows.length)}` : ""}
          </span>
          <span className="min-w-0 truncate text-faint">{data.updatedAt ? `relevé ${relative(data.updatedAt)}` : "stock jamais relevé"}</span>
          <span className="flex-1" />
          {can("sync.run") && (
            <button type="button" disabled={refreshing} onClick={() => void refresh()} className="flex-shrink-0 text-[15px] text-accent disabled:opacity-50">
              {refreshing ? "Actualisation…" : "Actualiser"}
            </button>
          )}
        </div>
        {error && <ErrorState error={new Error(error)} className="mb-2" />}

        {list.length === 0 ? (
          <Empty className="rounded-ios bg-white">{rows.length === 0 ? "Aucun produit en stock." : "Aucun produit ne correspond à ces filtres."}</Empty>
        ) : (
          <IosGroup>
            {list.slice(0, shown).map((p) => (
              <ProductRow key={p.productId} p={p} onOpen={() => openProduct({ id: p.id, name: p.name, scope: "boutique", boutiqueId })} />
            ))}
          </IosGroup>
        )}
      </div>
      {list.length > shown && <MoreButton onClick={() => setShown((s) => s + PAGE)}>Voir plus ({num(list.length - shown)})</MoreButton>}
      <div className="px-4 text-[12px] text-faint">« Vendu total » {data.salesSince ? `depuis ${dateFR(data.salesSince)}` : "sur tout l'historique"}</div>
    </>
  );
}

/** Ligne produit : prix d'achat → vente, ventes 30 j / total, réassort, statut ; stock à droite. */
function ProductRow({ p, onOpen }: { p: StockRowDTO; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 bg-white pl-4 text-left active:bg-[oklch(0.95_0.004_145)]">
      <span className="ios-sep flex min-w-0 flex-1 items-center gap-3 py-[11px] pr-4">
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 break-words text-[16px] leading-[1.3]">{p.name}</span>
          <span className="mt-[2px] block text-[13px] text-muted">
            <span className="tabular">{eur2(p.supplyPrice)}</span> → <span className="tabular text-ink">{eur2(p.price)}</span> · Vendu 30 j :{" "}
            <span className="tabular">{num(p.sold30)}</span>
          </span>
          <span className="block text-[13px] text-faint">
            {p.category ? `${p.category} · ` : ""}Total {num(p.soldTotal)} · Réassort {p.lastRestockAt ? dayMonth(p.lastRestockAt) : "—"}
          </span>
          <span className="mt-[5px] flex flex-wrap gap-[6px]">
            <StockBadge status={p.status} className="!rounded-[6px] !px-[7px] !py-[2px] !text-[12px] !font-semibold" />
            {p.promo && <span className="rounded-[6px] bg-accent px-[7px] py-[2px] text-[12px] font-semibold text-white">{p.promo.label}</span>}
          </span>
        </span>
        <span className="flex-shrink-0 text-right">
          <span className={cx("block text-[22px] font-bold leading-none tabular", p.stock <= 0 && "text-danger")}>{num(p.stock)}</span>
          <span className="mt-[2px] block text-[12px] text-muted">en stock</span>
        </span>
        <IosChevron />
      </span>
    </button>
  );
}
